// @flow

import React from "react";
import moment from "moment";
import { localize } from "redux-i18n";
import { GSGrid, View, Text } from "@components";
import config from "@config";
import { formatDate } from "@transformers/date";
import type { PositionLinkedAbsence } from "@modules/employee/reducer/employment-details/types";

const day = (value: ?string): string => (value ? moment(value).format(config.dateFormatBE) : "");

export function isLinkValid(
  absence: PositionLinkedAbsence,
  activeFrom: ?string,
  activeTo: ?string,
): boolean {
  const startsAfterPositionStart =
    !activeFrom || !absence.startsAt || day(absence.startsAt) >= day(activeFrom);
  const rtwBeforePositionEnd = !activeTo || !absence.rtwAt || day(absence.rtwAt) <= day(activeTo);

  return startsAfterPositionStart && rtwBeforePositionEnd;
}

type Props = $Exact<{
  linkedAbsences: ?Array<PositionLinkedAbsence>,
  activeFrom: ?string,
  activeTo: ?string,
  ...PropsFromLocalize,
}>;

export function LinkedAbsences(props: Props) {
  const { linkedAbsences, activeFrom, activeTo, t } = props;

  if (!linkedAbsences) return null;

  const columns = [
    {
      key: "absenceId",
      name: t("ABSENCE_ID"),
    },
    {
      key: "startsAt",
      name: t("START_DATE"),
      renderCell: ({ row }: Object) => formatDate(row.startsAt),
    },
    {
      key: "rtwAt",
      name: t("RTW_DATE"),
      renderCell: ({ row }: Object) => formatDate(row.rtwAt),
    },
    {
      key: "status",
      name: t("STATUS"),
      renderCell: ({ row }: Object) => GSGrid.render.statusPill(t(row.status)),
    },
    {
      key: "link",
      name: t("LINK"),
      width: 50,
      cellTooltip: {
        text: t("LINKED_ABSENCE_BLOCKED_TOOLTIP"),
        hide: ({ row }: Object) => isLinkValid(row, activeFrom, activeTo),
      },
      renderCell: ({ row }: Object) =>
        isLinkValid(row, activeFrom, activeTo)
          ? GSGrid.render.materialIcon("check_circle", "positive")
          : GSGrid.render.materialIcon("block", "negative"),
    },
  ];

  return (
    <View gap={2}>
      <Text type="h5" text={t("LINKED_ABSENCES")} />
      {linkedAbsences.length ? (
        <View gap={4}>
          <Text text={t("THIS_POSITION_IS_USED_IN_ABSENCES")} />
          <GSGrid columns={columns} rows={linkedAbsences} />
        </View>
      ) : (
        <Text text={t("THIS_POSITION_IS_NOT_LINKED_TO_ABSENCES")} />
      )}
    </View>
  );
}

export default localize()(LinkedAbsences);
